import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import '../../../libs/css/dataTable.css';
import FilterComponent from './FilterComponent';
import { format } from 'date-fns';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { API_ROUTES } from '../../../utils/constants';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';


function RevenueReportTable(props) {
    const { columns, dataList, setDataList, setPage, isFade, setIsFade, isAuthExpired, objectToFormData } = props;

    const [isLoading1, setIsLoading1] = useState(true);
    const [isLoading2, setIsLoading2] = useState(false);
    const [operatorList, setOperatorList] = useState([]);
    const [distinctList, setDistinctList] = useState([]);
    const [serviceProds, setServiceProds] = useState([]);
    const [dailyBreakdown, setDailyBreakdown] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [expandedRows, setExpandedRows] = useState({});
    const userData = JSON.parse(localStorage.getItem('userDataFs'));

    const isRowExpandable = (row) => row.bearer_ids && row.bearer_ids.length > 0;

    const handleRowExpandToggled = (expanded, row) => {
        if (expanded) {
            setExpandedRows({ [row.date]: true });
        } else {
            setExpandedRows({});
        }
    };

    const conditionalRowStyles = [
        {
            when: row => isRowExpandable(row),
            style: {
                cursor: 'pointer',
            },
        }
    ];

    // Airtel's bearer_ids/bearer_ids_revenue can carry the same bearerId more than once,
    // split by chargeType ("New" / "Renewal"). Match revenue by bearerId + chargeType
    // together so each charge type gets its own correct figure instead of the first match.
    const findBearerRevenue = (data, bearer) => {
        const revenueEntry = (data.bearer_ids_revenue || []).find(r =>
            r.bearerId === bearer.bearerId &&
            (bearer.chargeType ? r.chargeType === bearer.chargeType : true)
        );
        return parseFloat(revenueEntry?.Revenue || 0);
    };

    const BearerIdCard = ({ data, bearer, cardKey }) => {
        const revenueValue = findBearerRevenue(data, bearer);
        return (
            <div key={cardKey} className="col-lg-4 col-md-6 mb-2">
                <div className="card border-left-primary" style={{
                    borderLeft: '4px solid #2491e4',
                    transition: 'transform 0.2s',
                    cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.closest('.card').style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.closest('.card').style.transform = 'translateY(0)'}
                >
                    <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">Channel</small>
                                <div className="font-weight-bold" style={{
                                    fontSize: '0.9rem',
                                    color: '#2491e4',
                                    fontFamily: 'monospace'
                                }}>
                                    {bearer.bearerId}
                                </div>
                            </div>
                            <div className="text-right">
                                <small className="text-muted">Count</small>
                                <div className="h5 text-primary mb-0">
                                    {parseFloat(bearer.count || 0).toLocaleString()}
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2 pt-2" style={{ borderTop: '1px solid #eee' }}>
                            <small className="text-muted">Revenue</small>
                            <div className={`h6 mb-0 ${revenueValue > 0 ? 'text-success' : 'text-muted'}`}>
                                {`₦${revenueValue.toLocaleString()}`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const BearerIdBadges = ({ data }) => {
        if (!isRowExpandable(data)) {
            return (
                <div className='text-left th_data' style={{
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    margin: '10px 0'
                }}>
                    <div className="text-center py-4">
                        <i className="bx bx-info-circle text-muted" style={{ fontSize: '2rem' }}></i>
                        <p className="text-muted mt-2">No bearer ID data available for this date.</p>
                    </div>
                </div>
            );
        }

        // Group by chargeType (Airtel sends "New" / "Renewal"). Operators without a
        // chargeType fall into a single "All" group, which renders exactly as before.
        const groups = data.bearer_ids.reduce((acc, bearer) => {
            const key = bearer.chargeType || 'All';
            (acc[key] = acc[key] || []).push(bearer);
            return acc;
        }, {});
        const groupKeys = Object.keys(groups);
        const hasChargeTypes = !(groupKeys.length === 1 && groupKeys[0] === 'All');

        return (
            <div className='text-left th_data' style={{
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                margin: '10px 0'
            }}>
                {hasChargeTypes ? (
                    groupKeys.map(chargeType => (
                        <div key={chargeType} className="mb-3">
                            <h6 className="mb-2" style={{
                                color: '#2491e4',
                                textTransform: 'uppercase',
                                fontSize: '1.2rem',
                                letterSpacing: '0.5px'
                            }}>
                                {chargeType}
                            </h6>
                            <div className="row">
                                {groups[chargeType].map((bearer, index) => (
                                    <BearerIdCard key={`${chargeType}-${index}`} data={data} bearer={bearer} />
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="row">
                        {groups['All'].map((bearer, index) => (
                            <BearerIdCard key={index} data={data} bearer={bearer} />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const getOperators = async () => {
        setIsLoading1(true);
        try {
            const response = await fetch(API_ROUTES.OPERATORS, {
                method: "POST",
                headers: { 'token': userData?.responseDetails?.token },
            });
            const responseJSON = await response.json();
            isAuthExpired(responseJSON);
            if (responseJSON.status.toString() === "200") {
                const responseData = responseJSON.data;
                setOperatorList(responseData);
                setIsLoading1(false);
            }
        } catch (error) {
            console.log('Error getting data: ', error);
            setOperatorList([]);
            setIsLoading1(false);
        }
    }

    const distinctServices = async () => {
        setIsLoading1(true);
        try {
            const response = await fetch(API_ROUTES.DISTINCT_SERVICES, {
                method: "POST",
                headers: { 'token': userData?.responseDetails?.token },
            });
            const responseJSON = await response.json();
            isAuthExpired(responseJSON);
            if (responseJSON.status.toString() === "200") {
                const responseData = responseJSON.data;
                setDistinctList(responseData);
                setIsLoading1(false);
            }
        } catch (error) {
            console.log('Error getting data: ', error);
            setDistinctList([]);
            setIsLoading1(false);
        }
    }

    const serviceProducts = async (serviceId) => {
        setIsLoading2(true);
        const dataString = {
            service_id: serviceId,
        };
        try {
            const response = await fetch(API_ROUTES.SERVICE_PRODUCTS, {
                method: "POST",
                body: objectToFormData(dataString),
                headers: { 'token': userData?.responseDetails?.token },
            });
            const responseJSON = await response.json();
            isAuthExpired(responseJSON);
            if (responseJSON.status.toString() === "200") {
                const responseData = responseJSON.data;
                setServiceProds(responseData);
                setIsLoading2(false);
            }
        } catch (error) {
            console.log('Error getting data: ', error);
            setServiceProds([]);
            setIsLoading2(false);
        }
    }

    const submitSearchQuery = async (startDate, endDate, oprt, distinct_service_id, product_id) => {
        if (!startDate || !endDate) {
            alert('Please select both start and end dates');
            return;
        }

        const start = new Date(startDate);
        const end = new Date(endDate);

        // Optional: Validate date range isn't too large (e.g., max 1 year)
        const maxDateRange = 365 * 24 * 60 * 60 * 1000;
        if ((end - start) > maxDateRange) {
            alert('Date range cannot exceed 1 year');
            return;
        }

        setIsFade(true);
        const dataString = {
            start_date: startDate,
            end_date: endDate.replace("00:00:00", "23:59:59"),
            operator: oprt,
            distinct_service_id,
            product_id,
        };
        try {
            const response = await fetch(API_ROUTES.SERVICE_REVENUE_WITH_DAYS, {
                method: "POST",
                body: objectToFormData(dataString),
                headers: { 'token': userData?.responseDetails?.token },
            });
            const responseJSON = await response.json();
            isAuthExpired(responseJSON);
            setIsFade(false);
            if (responseJSON.status.toString() === "200") {
                const responseData = responseJSON.data;
                const formattedStartDate = startDate.split(' ')[0];
                const formattedEndDate = endDate.split(' ')[0];

                let operatorName = "All Operator";
                if (oprt === 1 || oprt === "1") {
                    operatorName = "MTN";
                } else if (oprt === 2 || oprt === "2") {
                    operatorName = "GLO";
                } else if (oprt === 3 || oprt === "3") {
                    operatorName = "Airtel";
                } else if (oprt === 4 || oprt === "4") {
                    operatorName = "9Mobile";
                }

                // Set summary data
                if (responseData.summary) {
                    setTotalRevenue(responseData.summary.totalChargeAmount);
                    setTotalCount(responseData.summary.totalCount);
                }

                // Set daily breakdown data
                if (responseData.daily_breakdown) {
                    setDailyBreakdown(responseData.daily_breakdown);
                }

                setDataList([
                    {
                        date: <div className="text-right" style={{ fontSize: '14px' }}><div><b>From:</b> {formattedStartDate}</div><div><b>To:</b> {formattedEndDate}</div></div>,
                        operator: operatorName,
                        service: distinct_service_id || 'All Services',
                        tariff: product_id || 'All Tariffs',
                        counts: responseData.summary?.totalCount || '0',
                        revenue: responseData.summary?.totalChargeAmount || '0',
                    },
                ]);
            }
        } catch (error) {
            console.log('Error getting data: ', error);
            setDailyBreakdown([]);
            setIsFade(false);
        }
    }

    // Export to CSV function
    const exportToCSV = () => {
        if (dailyBreakdown.length === 0) {
            alert('No data available to export');
            return;
        }

        // Create CSV content
        let csvContent = "data:text/csv;charset=utf-8,";
        
        // Add summary section
        csvContent += "SUMMARY\n";
        csvContent += `Total Revenue,₦${parseFloat(totalRevenue).toLocaleString()}\n`;
        csvContent += `Total Count,${parseFloat(totalCount).toLocaleString()}\n\n`;
        
        // Add daily breakdown section
        csvContent += "DAILY BREAKDOWN\n";
        csvContent += "Date,Daily Revenue,Daily Count\n";
        
        dailyBreakdown.forEach(row => {
            const date = format(new Date(row.date), 'MMM d, yyyy');
            const revenue = parseFloat(row.dailyChargeAmount).toFixed(2);
            const count = row.dailyCount;
            csvContent += `${date},${revenue},${count}\n`;
        });

        // Create download link
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `revenue_report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export to Excel function (using HTML table method)
    const exportToExcel = () => {
        if (dailyBreakdown.length === 0) {
            alert('No data available to export');
            return;
        }

        // Create HTML table
        let htmlContent = `
            <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
            <head>
                <meta charset="utf-8">
                <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Revenue Report</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
            </head>
            <body>
                <table>
                    <tr><td colspan="3"><b>SUMMARY</b></td></tr>
                    <tr><td>Total Revenue</td><td>₦${parseFloat(totalRevenue).toLocaleString()}</td><td></td></tr>
                    <tr><td>Total Count</td><td>${parseFloat(totalCount).toLocaleString()}</td><td></td></tr>
                    <tr><td colspan="3"></td></tr>
                    <tr><td colspan="3"><b>DAILY BREAKDOWN</b></td></tr>
                    <tr><th>Date</th><th>Daily Revenue</th><th>Daily Count</th></tr>
        `;

        dailyBreakdown.forEach(row => {
            const date = format(new Date(row.date), 'MMM d, yyyy');
            const revenue = parseFloat(row.dailyChargeAmount).toFixed(2);
            const count = row.dailyCount;
            htmlContent += `<tr><td>${date}</td><td>${revenue}</td><td>${count}</td></tr>`;
        });

        htmlContent += `
                </table>
            </body>
            </html>
        `;

        // Create blob and download
        const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `revenue_report_${format(new Date(), 'yyyy-MM-dd')}.xls`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        (async () => {
            getOperators();
            distinctServices();

            // Load initial data with last month's date range
            const today = new Date();
            const lastMonth = new Date();
            lastMonth.setMonth(lastMonth.getMonth() - 1);

            const formatDateForAPI = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            };

            // Automatically load last month's data
            await submitSearchQuery(
                formatDateForAPI(lastMonth).split(' ')[0] + ' 00:00:00',
                formatDateForAPI(today).split(' ')[0] + ' 23:59:59',
                '', // all operators
                '', // all services
                ''  // all products
            );
        })()
    }, []);

    return (
        <div className="App">

            {
                isLoading1 ?
                    <LoadingSpinner />
                    :
                    <FilterComponent isAuthExpired={isAuthExpired} isLoading1={isLoading1} isLoading2={isLoading2} operatorList={operatorList} distinctList={distinctList} serviceProducts={serviceProducts} serviceProds={serviceProds} submitSearchQuery={submitSearchQuery} />
            }

            {/* Export Buttons */}
            {dailyBreakdown.length > 0 && (
                <div className="row mb-3">
                    <div className="col-md-12 text-right">
                        <button 
                            className="btn btn-success mr-2" 
                            onClick={exportToCSV}
                            style={{ marginRight: '10px' }}
                        >
                            <i className="fa fa-file-excel-o"></i> Export to CSV
                        </button>
                        <button 
                            className="btn btn-primary" 
                            onClick={exportToExcel}
                        >
                            <i className="fa fa-file-excel-o"></i> Export to Excel
                        </button>
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            {dataList.length > 0 && (
                <div className="row mt-3 mb-3">
                    <div className="col-md-6">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <h5 className="card-title">Total Revenue</h5>
                                <h3>
                                    {totalRevenue !== null && totalRevenue !== undefined ?
                                        `₦${parseFloat(totalRevenue).toLocaleString()}` :
                                        '₦0'}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <h5 className="card-title">Total Count</h5>
                                <h3>
                                    {totalCount !== null && totalCount !== undefined ?
                                        parseFloat(totalCount).toLocaleString() :
                                        '0'}
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ opacity: isFade ? '0.3' : 1 }} className='mb-4'>
                {
                    isFade ?
                        <LoadingSpinner />
                        :
                        <DataTable
                            className="services_datatable users"
                            title=""
                            columns={columns}
                            data={dataList}
                            highlightOnHover
                            onChangePage={page => setPage(page)}
                        />
                }
            </div>

            {/* Daily Breakdown Table */}
            {dailyBreakdown.length > 0 && (
                <div className="card mb-3">
                    <div className="card-header">
                        <h5>Daily Breakdown Details</h5>
                    </div>
                    <div className="">
                        <DataTable
                            className="services_datatable users"
                            columns={[
                                {
                                    name: 'Date',
                                    selector: row => format(new Date(row.date), 'MMM d, yyyy'),
                                    sortable: true,
                                    sortFunction: (a, b) => new Date(a.date) - new Date(b.date),
                                },
                                {
                                    name: 'Daily Revenue',
                                    selector: row => `₦${parseFloat(row.dailyChargeAmount).toLocaleString()}`,
                                    sortable: true,
                                    sortFunction: (a, b) => parseFloat(a.dailyChargeAmount) - parseFloat(b.dailyChargeAmount),
                                },
                                {
                                    name: 'Daily Count',
                                    selector: row => parseFloat(row.dailyCount).toLocaleString(),
                                    sortable: true,
                                    sortFunction: (a, b) => parseFloat(a.dailyCount) - parseFloat(b.dailyCount),
                                }
                            ]}
                            data={dailyBreakdown}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[10, 20, 30, 50]}
                            highlightOnHover
                            striped
                            responsive
                            defaultSortFieldId={1}
                            paginationComponentOptions={{
                                rowsPerPageText: 'Rows per page:',
                                rangeSeparatorText: 'of',
                                selectAllRowsItem: true,
                                selectAllRowsItemText: 'All',
                            }}
                            expandableRows
                            expandableRowsComponent={BearerIdBadges}
                            expandOnRowClicked
                            expandableRowsHideExpander={false}
                            onRowExpandToggled={handleRowExpandToggled}
                            expandableRowExpanded={row => expandedRows[row.date] || false}
                            expandableRowDisabled={row => !isRowExpandable(row)}
                            conditionalRowStyles={conditionalRowStyles}
                            noDataComponent={
                                        <div className="text-center py-4">
                                            <p className="text-muted">No data available</p>
                                        </div>
                                    }
                        />
                    </div>
                </div>
            )}

            {/* Daily Breakdown Chart */}
            {dailyBreakdown.length > 0 && (
                <div className="card mb-3">
                    <div className="card-header">
                        <h5>Daily Revenue Breakdown</h5>
                    </div>
                    <div className="card-body">
                        <div style={{ width: '100%', height: 400 }}>
                            <ResponsiveContainer>
                                <LineChart
                                    data={dailyBreakdown}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 5,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(date) => {
                                            const d = new Date(date);
                                            return `${d.getMonth() + 1}/${d.getDate()}`;
                                        }}
                                    />
                                    <YAxis yAxisId="left" />
                                    <YAxis yAxisId="right" orientation="right" />
                                    <Tooltip
                                        formatter={(value, name) => {
                                            if (name === 'Revenue') {
                                                return [`₦${parseFloat(value).toLocaleString()}`, name];
                                            }
                                            return [value, name];
                                        }}
                                    />
                                    <Legend />
                                    <Line
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="dailyChargeAmount"
                                        stroke="#8884d8"
                                        activeDot={{ r: 8 }}
                                        name="Revenue"
                                    />
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="dailyCount"
                                        stroke="#82ca9d"
                                        name="Count"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            

        </div>
    );
}

export default RevenueReportTable;